import type { PopupConfiguration, SDKContext, TriggerType } from "./types";
import { flushEvents, recordEvent, sendFriendLinkLanding } from "./logger";
import { setCooltimeCookie } from "./utils";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const OVERLAY_ID = "lr-popup-overlay";
const SHOW_CLASS = "lr-popup-is-show";

// ---------------------------------------------------------------------------
// CSS injection (all styles embedded — no external stylesheet required)
// ---------------------------------------------------------------------------

function buildCSS(config: PopupConfiguration, isMobile: boolean): string {
  const closeBtnSize = isMobile
    ? config.mobile_close_button_size
    : config.pc_close_button_size;
  const closeBtnOpacity = isMobile
    ? config.mobile_close_button_opacity / 100
    : config.pc_close_button_opacity / 100;

  return `
    #${OVERLAY_ID} {
      --close-btn-size: ${closeBtnSize}px;
      --close-btn-opacity: ${closeBtnOpacity};
      display: flex;
      align-items: center;
      justify-content: center;
      position: fixed;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      z-index: 2147483647;
      opacity: 0;
      visibility: hidden;
      transition: opacity 0.6s ease;
      background-color: rgba(0, 0, 0, 0.8);
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    #${OVERLAY_ID}.${SHOW_CLASS} {
      opacity: 1;
      visibility: visible;
    }

    .lr-popup-inner {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      max-width: 90vw;
      max-height: 80vh;
    }

    .lr-popup-close {
      position: absolute;
      right: calc(var(--close-btn-size) / -2);
      top: calc(var(--close-btn-size) / -2);
      width: var(--close-btn-size);
      height: var(--close-btn-size);
      border-radius: 50%;
      background: #010101;
      opacity: var(--close-btn-opacity);
      border: none;
      cursor: pointer;
      padding: 0;
      z-index: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      -webkit-tap-highlight-color: transparent;
    }

    .lr-popup-close::before,
    .lr-popup-close::after {
      content: "";
      position: absolute;
      width: 60%;
      height: 2px;
      background: #fff;
      border-radius: 1px;
    }

    .lr-popup-close::before {
      transform: rotate(45deg);
    }

    .lr-popup-close::after {
      transform: rotate(-45deg);
    }

    .lr-popup-close:focus-visible {
      outline: 2px solid #fff;
      outline-offset: 2px;
    }

    .lr-popup-link {
      display: block;
      width: auto;
      max-width: 90vw;
      max-height: 80vh;
      text-decoration: none;
      line-height: 0;
    }

    .lr-popup-img {
      display: block;
      max-width: 90vw;
      max-height: 80vh;
      width: auto;
      height: auto;
      object-fit: contain;
      border-radius: 4px;
    }
  `;
}

// ---------------------------------------------------------------------------
// DOM construction
// ---------------------------------------------------------------------------

function injectStyles(config: PopupConfiguration, isMobile: boolean): void {
  if (document.getElementById("lr-popup-styles")) return;
  const style = document.createElement("style");
  style.id = "lr-popup-styles";
  style.textContent = buildCSS(config, isMobile);
  document.head.appendChild(style);
}

function buildDOM(config: PopupConfiguration): HTMLDivElement {
  const overlay = document.createElement("div");
  overlay.id = OVERLAY_ID;

  const inner = document.createElement("div");
  inner.className = "lr-popup-inner";

  const closeBtn = document.createElement("button");
  closeBtn.className = "lr-popup-close";
  closeBtn.setAttribute("aria-label", "\u9589\u3058\u308B"); // 閉じる
  closeBtn.type = "button";

  const link = document.createElement("a");
  link.className = "lr-popup-link";
  link.href = "#";
  link.setAttribute("role", "link");
  link.setAttribute("aria-label", "\u30DD\u30C3\u30D7\u30A2\u30C3\u30D7\u753B\u50CF"); // ポップアップ画像

  const img = document.createElement("img");
  img.className = "lr-popup-img";
  img.src = config.media_url;
  img.alt = "\u30DD\u30C3\u30D7\u30A2\u30C3\u30D7\u753B\u50CF"; // ポップアップ画像
  img.loading = "eager";

  link.appendChild(img);
  inner.appendChild(closeBtn);
  inner.appendChild(link);
  overlay.appendChild(inner);

  return overlay;
}

// ---------------------------------------------------------------------------
// Show / hide logic
// ---------------------------------------------------------------------------

/** Currently mounted overlay element (null if not showing). */
let currentOverlay: HTMLDivElement | null = null;

/**
 * Show the popup overlay with fade-in animation.
 * Records a showPopup log event and sets the cooltime cookie.
 */
export function showPopup(ctx: SDKContext, trigger: TriggerType): void {
  // Already showing — do nothing
  if (currentOverlay) return;

  // Prevent body scroll while overlay is visible
  const originalOverflow = document.body.style.overflow;

  injectStyles(ctx.config, ctx.isMobile);
  const overlay = buildDOM(ctx.config);
  document.body.appendChild(overlay);
  currentOverlay = overlay;

  // Force reflow before adding show class (needed for transition)
  void overlay.offsetHeight;

  overlay.classList.add(SHOW_CLASS);
  document.body.style.overflow = "hidden";

  // Record showPopup event
  recordEvent("showPopup", trigger);

  // Set cooltime cookie
  setCooltimeCookie(
    String(ctx.config.popup_config_group_id),
    ctx.config.suppression_duration_for_redisplay,
  );

  // Mark popup as force-hidden to prevent other triggers from firing
  window.isPopupForceHidden = true;

  // --- Event handlers ---

  const handleClose = () => {
    recordEvent("closePopup", trigger);
    hidePopup(originalOverflow);
  };

  const handleClick = async (e: Event) => {
    e.preventDefault();
    recordEvent("clickPopup", trigger);

    // Flush logs and track LINE transition before redirecting
    await Promise.all([flushEvents(), sendFriendLinkLanding()]);

    hidePopup(originalOverflow);

    // Navigate to destination URL
    window.location.href = ctx.config.destination_url;
  };

  // Close button
  const closeBtn = overlay.querySelector(".lr-popup-close");
  closeBtn?.addEventListener("click", handleClose);

  // Image / link click
  const link = overlay.querySelector(".lr-popup-link");
  link?.addEventListener("click", handleClick);

  // Close on overlay background click (not bubble from inner)
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) {
      handleClose();
    }
  });

  // Close on Escape key (accessibility)
  const handleKeydown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      handleClose();
      document.removeEventListener("keydown", handleKeydown);
    }
  };
  document.addEventListener("keydown", handleKeydown);
}

/**
 * Hide and remove the popup overlay.
 */
function hidePopup(originalOverflow: string): void {
  if (!currentOverlay) return;

  currentOverlay.classList.remove(SHOW_CLASS);

  // Wait for fade-out transition to complete before removing DOM
  const overlay = currentOverlay;
  const onTransitionEnd = () => {
    overlay.removeEventListener("transitionend", onTransitionEnd);
    overlay.remove();
  };
  overlay.addEventListener("transitionend", onTransitionEnd);

  // Fallback: remove after 700ms if transitionend doesn't fire
  setTimeout(() => {
    if (overlay.parentNode) {
      overlay.remove();
    }
  }, 700);

  document.body.style.overflow = originalOverflow;
  currentOverlay = null;
}

/**
 * Check if the popup is currently visible.
 */
export function isPopupVisible(): boolean {
  return currentOverlay !== null;
}
